import { useEffect } from "react";
import { X, ShieldAlert, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

const menuLinks = [
  { label: "Mission", to: "/mission" },
  { label: "Labs", to: "/labs" },
  { label: "Success Stories", to: "/success-stories" },
  { label: "Careers", to: "/careers" },
  { label: "Contact", to: "/contact" },
];

export default function AuroraSlideMenu({ open, onClose }) {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <div
      data-testid="aurora-slide-menu"
      className={`fixed inset-0 z-[60] ${
        open ? "pointer-events-auto" : "pointer-events-none"
      }`}
      style={open ? {} : { visibility: "hidden" }}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-500 ${
          open ? "opacity-100" : "opacity-0"
        }`}
      />

      {/* Panel */}
      <div
        className={`absolute inset-y-0 left-0 w-full sm:max-w-md bg-[#0A0A0C] border-r border-white/[0.06] transform transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Radial ruby glow */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full bg-[#FF3B6B]/10 blur-[100px]" />
          <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-[#FF3B6B]/[0.06] blur-[120px]" />
        </div>

        <div className="relative h-full flex flex-col p-8 sm:p-10">
          {/* Top bar */}
          <div className="flex items-center justify-between mb-12">
            <Link
              to="/safety-center"
              data-testid="menu-safety-center-btn"
              onClick={onClose}
              className="group inline-flex items-center gap-2 rounded-full pl-3 pr-4 h-9 border border-[#FF3B6B]/30 bg-[#FF3B6B]/[0.08] hover:bg-[#FF3B6B]/[0.15] hover:border-[#FF3B6B]/50 transition-all"
              style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
            >
              <ShieldAlert size={14} className="text-[#FF3B6B]" />
              <span className="text-white text-xs font-medium tracking-wide">
                Safety Center
              </span>
              <ArrowUpRight size={12} className="text-white/50 group-hover:text-white transition-colors" />
            </Link>

            <button
              data-testid="menu-close-btn"
              onClick={onClose}
              className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:border-white/30 transition-all"
              aria-label="Close menu"
            >
              <X size={16} strokeWidth={1.6} />
            </button>
          </div>

          {/* Links */}
          <nav className="flex-1 flex flex-col justify-center">
            <ul className="space-y-2">
              {menuLinks.map((link, idx) => (
                <li
                  key={link.to}
                  className={`transform transition-all duration-500 ${
                    open
                      ? "translate-y-0 opacity-100"
                      : "translate-y-4 opacity-0"
                  }`}
                  style={{ transitionDelay: open ? `${150 + idx * 70}ms` : "0ms" }}
                >
                  <Link
                    to={link.to}
                    onClick={onClose}
                    data-testid={`menu-link-${link.to.replace("/", "")}`}
                    className="group block py-3"
                  >
                    <span
                      style={{ fontFamily: "'Bodoni Moda', serif" }}
                      className="text-4xl sm:text-5xl text-white tracking-tight transition-colors group-hover:text-[#FF3B6B]"
                    >
                      {link.label}
                    </span>
                    <span className="block h-[1px] w-0 group-hover:w-16 bg-[#FF3B6B] transition-all duration-300 mt-1" />
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Footer of menu */}
          <div className="pt-8 border-t border-white/[0.06]">
            <p
              className="text-white/40 text-[10px] uppercase tracking-[0.25em]"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              Aurora — The New Beginning
            </p>
            <p className="text-white/30 text-xs mt-1" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              Where trust leads to real love.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
